import { Component, OnInit, Renderer2, ViewChild } from '@angular/core';
import { IonContent, IonHeader, IonInfiniteScroll, PopoverController } from '@ionic/angular';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';

import { InfoPopupComponent } from '../../shared/components/infopopup/infopopup';
import { ArticlesService } from '../../services/articles.service';
import { Oneperweek } from '../../services/oneperweek';
import { Multitenant } from '../../services/multitenant.provider';
import { MetaProvider } from '../../services/meta.provider';
import { Logout } from '../../services/logout.provider';
import { AuthProvider } from '../../services/auth.provider';
import { ConfigService } from '../../services/config.service';
import { GaEventTrackerProvider } from '../../services/gaeventtracker.provider';
import { PopUpService } from '../../shared/components/pop-up/pop-up.service';

import { Publicity } from '../../models/publicity.model';
import { PopUp } from '../../models/popup.model';
import { Article } from '../../models/article.model';
import { filter } from 'rxjs/internal/operators/filter';
import { VIDEONEWSPAPERS, formatDatePublish, newspaperPdfOrder } from 'src/app/shared/utils';

@Component({
  selector: 'app-home',
  templateUrl: 'home.html',
  styleUrls: ['home.scss'],
})
export class HomePage implements OnInit {

  @ViewChild(IonContent) content: IonContent;
  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;
  @ViewChild(IonHeader) header: IonHeader;

  public articles: Array<Article>;
  public error: string;
  public themeClass: string;
  public today: Date;
  public authenticated: boolean;
  public search = false;
  public searchResults: Array<Article>;
  public showOpenPerWeek: boolean;
  public isActivePage = 'home';
  public video: { [key: number]: boolean } = {};
  public tempPopUp: boolean;
  public isTimelineFilter: string;
  public popup: PopUp = new PopUp();
  public ua: string;
  public category: string;
  public date: string;
  public showSpinner: boolean;
  public dateIndexes = [];
  private searchTerm: string;
  private pub: Publicity;
  private offset = 15;
  private loadingResults = false;
  private offsetSearch = 15;
  private start = 0;
  public showNewspaperPdf: boolean; // remove newspaper pdf if filter by une-carriere-a-la-loupe-aeco
  public newspapersPdf = [];
  private pdfAccess = [];
  public formatDatePublish = formatDatePublish;
  public currentPosition = window.scrollY;

  constructor(
    public auth: AuthProvider,
    private router: Router,
    private actRoute: ActivatedRoute,
    private articlesService: ArticlesService,
    private oneperweek: Oneperweek,
    private logout: Logout,
    public multiTenant: Multitenant,
    private config: ConfigService,
    private popoverCtrl: PopoverController,
    private meta: MetaProvider,
    private ga: GaEventTrackerProvider,
    private popUpService: PopUpService,
    public renderer: Renderer2
  ) { }

  ngOnInit() {
    this.themeClass = this.multiTenant.getDomainClass();
    this.showNewspaperPdf = true;
    this.today = new Date();
    this.isActivePage = 'home';

    this.actRoute.queryParamMap.subscribe(
      params => {
        this.start = 0;
        this.articles = [];
        this.searchResults = [];
        if (params.get('tempPopUp')) {
          this.tempPopUp = true;
          const isExcludedFromPaywall = params.get('isExcludedFromPaywall');
          if (isExcludedFromPaywall) {
            this.popup.type = 'excludedFromPaywall';
            this.popup.data = params.get('newspaper');
          } else {
            this.popup.type = 'five';
            this.popup.data = this.oneperweek.remainingDays.toString();
          }
        }
        this.isTimelineFilter = params.get('filter');
        this.category = params.get('category');
        this.date = params.get('date');

        if (this.auth.isAuthenticated()) {
          if (this.isTimelineFilter) {
            this.getArticles(this.isTimelineFilter, this.category, this.start, this.offset, this.date);
            this.getPublicity(this.isTimelineFilter);
          } else {
            if (this.themeClass === 'mwjp') {
              this.getArticles('mwjp', '', this.start, this.offset, null);
            } else if (this.themeClass === 'mwde') {
              this.getArticles('mwde', '', this.start, this.offset, null);
            } else if (this.themeClass === 'clfy') {
              this.getArticles('clfy', '', this.start, this.offset, null);
            } else {
              this.getArticles('', '', this.start, this.offset, null);
            }
          }
          this.authenticated = true;
          if (this.themeClass === 'aeco' && this.category === 'une-carriere-a-la-loupe-aeco') {
            this.showNewspaperPdf = false;
          }
        } else {
          if (this.auth.logedWithNoProfiles()) {
            this.router.navigate(['creer-compte', 'etape-2']);
          } else if (this.auth.isPartiallyAuthenticated()) {
            this.router.navigate(['profiles']);
          } else {
            this.authenticated = false;
            this.getArticles(this.themeClass, '', this.start, this.offset, null);
            if (this.themeClass !== 'playbacpresse') {
              this.getPublicity(this.themeClass);
            }
          }
        }
      }
    );

    // In case we search for something and then presse home button.
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      if (this.searchResults && this.searchResults.length > 0) {
        this.router.routeReuseStrategy.shouldReuseRoute =  () => false;
      }
    });
  }

  ionViewWillEnter(): void {
    this.ua = this.getUserUa();

    this.newspapersPdf = [];
    this.setPDFAccess();
    this.renderer.setStyle(this.header['el'], 'webkitTransition', 'margin-top 700ms');
  }

  ionViewDidEnter() {
    this.meta.clearHead();
    const seo = this.config.getSeoConfig();
    if (seo[this.themeClass]) {
      const homeTitle = seo[this.themeClass].home.title;
      const homeDescription = seo[this.themeClass].home.description;
      this.meta.addTitle(homeTitle);
      this.meta.addDescription(homeDescription);
    }

    if (this.auth.isAuthenticated() && !this.isTimelineFilter) {
      const access = this.auth.hasAccessTo();
      const arrayAccess = access.split(',');
      let singleAccess: string;
      if (arrayAccess.length === 1) {
        singleAccess = arrayAccess.toString();
        this.ga.trackEvent( 'playbacpresse', 'chargement timeline', singleAccess, 0 );
      }
    }

    if (!this.auth.isAuthenticated() && this.popUpService.havePassedSevenDays()) {
      this.tempPopUp = true;
      this.popup.type = 'unIdentifiedUser';
    }
  }

  public getArticles(newspaper: string, category: string, start: number, limit: number, date: string) {
    this.isActivePage = 'home';
    this.showSpinner = true;

    this.articlesService.getArticles(newspaper, category, start, limit, date).subscribe({
      next:(res) => {
        const response = this.sortArticlesByPublishOnAndNewspaperSubname(res, 'acne');
        if (!this.articles) {
          this.articles = response;
          if (this.pub) {
            this.articles.splice(3, 0, this.pub);
          }
        } else {
          this.articles = this.articles.concat(response);
        }
        if (this.authenticated) {
          this.articles.forEach( (value: Article, index: number, array: Article[]) => {
            if ( value.article_content && value.article_content.length >= 221 && value.article_content.length <= 559) {
              array[index].article_content_excerpt = value.article_content.slice(0, 220) + ' ...';
              array[index].article_content_excerpt_share = value.article_content.slice(0, 220).replace(/(<([^>]+)>)/gi, '') + ' ...';
              array[index].article_mail_description =
                'Cet article a été sélectionné dans ' + value.newspaper_name + ' du ' + value.publish_on + '.\n' +
                array[index].article_content_excerpt_share;
              array[index].readmore = 1;
            }

            if ( value.article_content && value.article_content.length > 559 ) {
              array[index].article_content_excerpt = value.article_content.slice(0, 220) + ' ...';
              array[index].article_content_excerpt_share = value.article_content.slice(0, 220).replace(/(<([^>]+)>)/gi, '') + ' ...';
              array[index].article_mail_description =
                'Cet article a été sélectionné dans ' + value.newspaper_name + ' du ' +
                value.publish_on + '.\n' + array[index].article_content_excerpt_share;
              array[index].readmore = 2;
            }

            if ( value.article_content && value.article_content.length <= 220 ) {
              array[index].article_content_excerpt = value.article_content;
              if (value.article_category !== 'LE COIN DES INCOLLABLES') {
                array[index].readmore = 0;
              } else {
                array[index].readmore = 1;
              }
            }

            if (value.article_title) {
              array[index].article_title_caps = value.article_title.toUpperCase();
            }

            if (value.publish_on) {
              array[index].publish_on_Obj = formatDatePublish( value.publish_on );
            }

            if ( value.newspaper_subname === 'lepq' || value.newspaper_subname === 'mlwk' ) {
              array[index].include = ['email'];
            }
            if (
              value.newspaper_subname === 'mquo' ||
              value.newspaper_subname === 'week' ||
              value.newspaper_subname === 'woch' ||
              value.newspaper_subname === 'sema'
            ) {
              array[index].include = ['facebook', 'email'];
            }
            if ( value.newspaper_subname === 'actu' || value.newspaper_subname === 'aeco' ) {
              array[index].include = [ 'facebook', 'twitter', 'email', 'whatsapp' ];
            }
          });
        } else {
          this.articles.forEach( (value: Article, index: number, array: Article[]) => {
            this.video[value.article_id] = false;
            if (value.article_content) {
              array[index].article_content_excerpt = value.article_content.slice(0, 220) + ' ...';
              array[index].article_content_excerpt_share = value.article_content.slice(0, 220).replace(/(<([^>]+)>)/gi, '') + ' ...';
              array[index].article_mail_description =
                'Cet article a été sélectionné dans ' + value.newspaper_name + ' du ' + value.publish_on + '.\n' +
                array[index].article_content_excerpt_share;
              array[index].readmore = 2;
            }
            if (value.article_title) {
              array[index].article_title_caps = value.article_title.toUpperCase();
            }

            if (value.publish_on) {
              const dateObj = formatDatePublish(value.publish_on);
              array[index].publish_on_Obj = dateObj;
            }

            if (value.newspaper_subname === 'lepq' || value.newspaper_subname === 'mlwk' ) {
              array[index].include = ['email'];
            }
            if (
              value.newspaper_subname === 'mquo' ||
              value.newspaper_subname === 'week' ||
              value.newspaper_subname === 'woch' ||
              value.newspaper_subname === 'sema'
            ) {
              array[index].include = ['facebook', 'email'];
            }
            if ( value.newspaper_subname === 'actu' || value.newspaper_subname === 'aeco' ) {
              array[index].include = [ 'facebook', 'twitter', 'email', 'whatsapp'];
            }
          });
        }
        this.calculateDates();
        setTimeout(() => {
          const timeframe = document.getElementsByTagName('iframe');
          if (timeframe.length > 0) {
            for (let i = 0; i < timeframe.length; i++) {
              timeframe[i].width = '100%';
            }
          }
        }, 1000);
      },
      error:(error) => {
        if ( error.message === 'Invalid tokens.' && this.auth.isPartiallyAuthenticated() ) {
          this.router.navigate(['profiles']);
        } else if (error.message === 'Invalid tokens.') {
          this.logout.logOut();
          this.router.navigate(['']);
        }
        this.error = ' Vos identifiants ne sont pas reconnus';

        this.showSpinner = false;
        this.loadingResults = false;
        if (this.infiniteScroll) {
          this.infiniteScroll.complete();
        }
      },
      complete:() => {
        this.showSpinner = false;
        this.loadingResults = false;
        if (this.infiniteScroll) {
          this.infiniteScroll.complete();
        }
      }
  });
  }
  private sortArticlesByPublishOnAndNewspaperSubname(articles: Article[], newspaperSubname: string): Article[] {

    return articles.sort((a, b) => {
      // Prioritize articles with article_on_the_top
      if (a.article_on_the_top && !b.article_on_the_top) return -1;
      if (b.article_on_the_top && !a.article_on_the_top) return 1;

      // Convert DD/MM/YY to Date objects (handling 2-digit year)
      const parseDate = (dateStr: string) => {
        const [day, month, year] = dateStr.split('/').map(Number);
        return new Date(2000 + year, month - 1, day); // Assumes 21st century (20XX)
      };

      const dateA = parseDate(a.publish_on);
      const dateB = parseDate(b.publish_on);

      // Sort by date (newest first)
      if (dateB > dateA) return 1;
      if (dateB < dateA) return -1;

      // If dates are equal, prioritize newspaperSubname
      if (a.newspaper_subname === newspaperSubname && b.newspaper_subname !== newspaperSubname) return -1;
      if (b.newspaper_subname === newspaperSubname && a.newspaper_subname !== newspaperSubname) return 1;

      // If same date and same newspaper, maintain original order
      return 0;
    });

  }

  public calculateDates() {
    let lastarticleDate = '';
    this.dateIndexes = [];
    this.articles.forEach((article, index) => {
      if (article.publish_on !== lastarticleDate && !article.article_on_the_top && !article.article_sponsored) {
        this.dateIndexes.push(index);
        lastarticleDate = article.publish_on;
        if (this.auth.isAuthenticated()) {
          this.getPdfForNewspapers(formatDatePublish(lastarticleDate));
        }
      }
    });
  }

  private setPDFAccess() {
    let news: any[] = [];

    if (this.auth.isAuthenticated() && this.auth.hasAccessToPdf()) {
      news = news || [];
      const newsAccPdf = this.auth.hasAccessToPdf().split(',');
      news = [...new Set([...news, ...newsAccPdf])];
    }

    if (this.auth.hasAccessTo() && this.auth.isAuthenticated()) {
      news = news || [];
      const newsAccess = this.auth.hasAccessTo().split(',');
      news = [...new Set([...news, ...newsAccess])];
    }

    this.pdfAccess = news.filter(el => el !== 'play').map((newspaper: string) => VIDEONEWSPAPERS[newspaper] ? VIDEONEWSPAPERS[newspaper] : newspaper);
  }

  public getPublicity(newspaper: string) {
    this.articlesService.getPubAdds(5, newspaper).subscribe(
      (response) => {
        this.pub = {
          access: true,
          article_type: 'livre-pub',
          header_color: response[0].book_header_color,
          tagline: response[0].book_tagline,
          book_title: response[0].book_title,
          article_content: response[0].book_various_content,
          article_url: response[0].book_slug,
          book_author: response[0].book_author,
          book_newspaper: response[0].book_newspaper,
          book_eliseuse_url: response[0].book_eliseuse_url,
          article_slug: response[0].book_slug,
          is_free: true,
          book_various_content: response[0].book_various_content,
          video: false,
          carrousel: false,
          book_description: response[0].book_description,
        };

        if (response[0].book_image) {
          this.pub.image_url = response[0].book_image.url;
        }

        if (response[0].book_logo) {
          this.pub.logo = response[0].book_logo.url;
        }

        if (response[0].book_edition_image) {
          this.pub.book_edition_image = {
            url: response[0].book_edition_image.url,
          };
        }

        if (response[0].book_cover) {
          this.pub.book_cover = { url: response[0].book_cover.url };
        }
      },
      (error) => {}
    );
  }

  public onNotify(message: string): void {
    if (this.searchResults && this.searchResults.length > 0 && message !== this.searchTerm) {
      this.start = 0;
    } else if (!this.searchResults) {
      this.start = 0;
    }

    if (this.auth.isAuthenticated()) {
      if (this.isTimelineFilter) {
        if (message !== undefined) {
          this.getSearchResults(message, this.isTimelineFilter, this.start, this.offset);
        }
      } else {
        if (message !== undefined) {
          if (this.themeClass === 'mwjp') {
            this.getSearchResults(message, 'mwjp', this.start, this.offset);
          } else if (this.themeClass === 'mwde') {
            this.getSearchResults(message, 'mwde', this.start, this.offset);
          } else {
            this.getSearchResults(message, '', this.start, this.offset);
          }
        }
      }
      this.authenticated = true;
    } else {
      this.authenticated = false;
      if (message !== undefined) {
        this.getSearchResults( message, this.themeClass, this.start, this.offset );
      }
    }
  }

  public onNotifyOnePerWeek(message: string): void {
    this.showOpenPerWeek = false;
  }

  public onNotifyPopUp(message: string): void {
    this.tempPopUp = false;
  }

  private getPdfForNewspapers(date: Date) {
    const currentDate = date ? date :  new Date();
    const newspaperPdfDate = `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}-${currentDate.getDate()}`;

    this.pdfAccess.forEach(newspaper => this.articlesService.getPDFByNewspaperAndDate(newspaper, newspaperPdfDate).subscribe({ next: data => this.concatenateNewspapersPdfs(data)}));
  }

  private concatenateNewspapersPdfs(data: any[]) {
    this.newspapersPdf = [...this.newspapersPdf, ...data].filter((v,i,a) => a.findIndex(v2 => (v2.newspaperPdf_id === v.newspaperPdf_id)) === i)
  }

  public getNewspapersPdfByDate(publish_on: string): any[] {
    if (this.isTimelineFilter) {
      return this.newspapersPdf.filter(el => el.publish_on === publish_on && el.newspaper_subname == this.isTimelineFilter).sort(newspaperPdfOrder);
    } 

    return this.newspapersPdf.filter(el => el.publish_on === publish_on).sort(newspaperPdfOrder);
  }

  public onBottomMenuNotify(message: string): void {
    this.tempPopUp = true;
    this.popup.type = 'featureToCome';
  }

  async presentPopover(myEvent, type) {
    const popover = this.popoverCtrl.create({
      component: InfoPopupComponent,
      componentProps: { page: 'home' },
      cssClass: 'feature-tooltip',
    });
    (await popover).present(myEvent);

    switch (type) {
      case 'share':
        this.ga.trackEvent(this.themeClass, 'Clic', 'Partager un article', 0);
        break;
      case 'fav':
        this.ga.trackEvent( this.themeClass, 'Clic', 'Ajout ​aux ​favoris ​d’un article', 0 );
        break;
      case 'comment':
        this.ga.trackEvent( this.themeClass, 'Clic', 'Commenter ​un ​article', 0 );
        break;
    }
  }

  public getSearchResults( searchString: string, newspaper: string, start: number, limit: number): void {
    this.articles = [];
    this.isActivePage = '';
    this.loadingResults = true;
    this.showSpinner = true;
    this.articlesService
      .getSearchResults(searchString, newspaper, start, limit)
      .subscribe(
        (response) => {
          if (!this.searchResults || this.searchTerm !== searchString) {
            this.searchResults = response;
            this.content.scrollToTop();
          } else {
            this.searchResults = this.searchResults.concat(response);
          }

          this.searchResults.forEach(
            (value: any, index: number, array: any) => {
              if (value.content) {
                array[index].content_excerpt = value.content.slice(0, 120) + ' ...';
                array[index].readmore = 0;
              }
              if (value.publish_on) {
                array[index].publishOnObj = new Date(value.publish_on);
              }
            }
          );
        },
        () => {},
        () => {
          this.showSpinner = false;
          this.loadingResults = false;
          this.searchTerm = searchString;
          if (this.infiniteScroll) {
            this.infiniteScroll.complete();
          }
        }
      );
  }

  public showTempPopUp() {
    if (this.themeClass !== 'playbacpresse') {
      this.tempPopUp = true;
      this.popup.type = 'hybridApp';
      this.popup.platform = this.themeClass;
    }
  }

  public doInfinite(event: any) {
    this.start = this.start + this.offset;
    if (this.auth.isAuthenticated()) {
      if (!this.isTimelineFilter) {
        if (this.articles && this.articles.length > 0) {
          if (this.themeClass === 'mwjp') {
            this.getArticles('mwjp', '', this.start, this.offset, null);
          } else if (this.themeClass === 'mwde') {
            this.getArticles('mwde', '', this.start, this.offset, null);
          } else {
            this.getArticles('', '', this.start, this.offset, null);
          }
        } else {
          if (this.themeClass === 'mwjp') {
            this.getSearchResults( this.searchTerm, 'mwjp', this.start, this.offsetSearch );
          } else if (this.themeClass === 'mwde') {
            this.getSearchResults( this.searchTerm, 'mwde', this.start, this.offsetSearch );
          } else {
            this.getSearchResults( this.searchTerm, '', this.start, this.offsetSearch );
          }
        }
      } else {
        if (this.articles.length > 0) {
          this.getArticles(this.isTimelineFilter, this.category, this.start, this.offset, this.date);
        } else {
          this.getSearchResults( this.searchTerm, this.isTimelineFilter, this.start, this.offsetSearch );
        }
      }
    } else {
      if (this.articles.length > 0) {
        this.getArticles(this.themeClass, '', this.start, this.offset, null);
      } else {
        this.getSearchResults( this.searchTerm, this.themeClass, this.start,  this.offsetSearch );
      }
    }
  }

  public onContentScroll(event:any) {
    let scroll = event.detail.scrollTop;
    if (scroll > 10 && scroll > this.currentPosition) {
      this.renderer.setStyle(this.header['el'], 'margin-top', this.authenticated ?  '-76px' : '-60px');
    } else {
      this.renderer.setStyle(this.header['el'], 'margin-top', '0px');
    }

    this.currentPosition = scroll;
  }

  public getUserUa() {
    const ua = window.navigator.userAgent;
    const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
    const webkit = !!ua.match(/WebKit/i);
    const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
    const isAndroid = !iOSSafari && navigator.userAgent.match(/android/i) != null;

    return isAndroid ? 'android' : 'ios';
  }

  public onShowOnePerWeek(popup: PopUp) {
    this.popup = popup;
  }

  public onActivatePopup(active: boolean) {
    this.tempPopUp = active;
  }

  public trackByFn(index, item) {
    return item.article_id;
  }
}
